import { DateTime } from 'luxon';

// df-efc66c4a2f6e (internal ticket #941) — pure decision rules for the food
// unpublish outbox. Kept free of Angular/PouchDB imports so the policy is
// unit-testable under mocha/ts-node (see food-unpublish-outbox-rules.test.ts),
// the same split as image-upload-outbox-rules.ts vs its service.

/**
 * Give-up thresholds. An item the server keeps REFUSING would retry on every
 * reconnect and every login for the rest of time; cap it and report the drop
 * instead (`trackSilentProblem('food-unpublish.replay-given-up')` — see
 * food-unpublish-outbox.service.ts). An item the server has never answered
 * about is deliberately not capped at all; `shouldDropUnpublishItem` below says
 * why, and what that accepts.
 *
 * These are deliberately LOOSER than the image outbox's 10 attempts / 14 days.
 * A queued photo that is dropped costs the user one picture and the harm stops
 * growing. A queued unpublish that is dropped leaves a food in the shared,
 * searchable food database for ever, and that harm does not expire — so it is
 * worth carrying the item across a long holiday with the phone in a drawer.
 */
export const UNPUBLISH_OUTBOX_MAX_ATTEMPTS = 20;
export const UNPUBLISH_OUTBOX_MAX_AGE_DAYS = 90;

/**
 * Decide whether a queued unpublish should be abandoned. Two arms, and only one
 * of them can fire on its own:
 *
 * - **Attempts**, unconditionally. `attempts` counts only drain attempts that
 *   actually reached the server, so this bounds an item the server keeps
 *   refusing.
 * - **Age**, but only once `attempts > 0`. An item the server has never answered
 *   about has never been asked, however old it is — dropping it would throw the
 *   removal away one moment after the connection that would have made it succeed
 *   came back.
 *
 * Why that second rule matters, and the two shapes of item it leaves queued for
 * ever, are in `design-docs/sync-integrity.md`, "The food unpublish outbox".
 */
export function shouldDropUnpublishItem(input: { attempts: number; ageDays: number }): boolean {
  if (input.attempts >= UNPUBLISH_OUTBOX_MAX_ATTEMPTS) {
    return true;
  }
  return input.attempts > 0 && input.ageDays > UNPUBLISH_OUTBOX_MAX_AGE_DAYS;
}

/**
 * The attempt count of one outbox document, read defensively.
 *
 * Anything that is not a finite, non-negative number reads as 0. A document read
 * from a database may have been written by an older build or be damaged, and
 * `CLAUDE.md` asks for that to be normalised as it is read — this field needs it
 * as much as the timestamp below, because an unusable count makes an item
 * UN-DROPPABLE and does so silently: `undefined >= 20` and `undefined > 0` are
 * both false, so neither arm of the rule above can ever fire, and `attempts += 1`
 * on it yields `NaN`, which PouchDB stores as `null` and which fails both arms
 * again on every later drain.
 *
 * Reading as 0 is the safe direction: it means "this item has not been asked
 * about yet", which costs at most one extra round of retries and is what a fresh
 * item says anyway.
 */
export function readAttemptCount(raw: unknown): number {
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : 0;
}

/**
 * The queued-at timestamp of one outbox document, read defensively, or
 * `undefined` when what is stored is not a usable ISO timestamp.
 *
 * A document read from a database may have been written by an older build of
 * this service or be damaged outright, and `CLAUDE.md` asks for that to be
 * normalised as it is read rather than trusted. It matters more than usual
 * here because the drain sorts by this field BEFORE its per-item loop: one
 * document whose `createdAt_utc` is missing or not a string would throw in the
 * comparator, which disables the WHOLE drain on every trigger from then on
 * rather than costing one item.
 */
export function readQueuedAtIso(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length === 0) {
    return undefined;
  }
  return DateTime.fromISO(raw).isValid ? raw : undefined;
}

/**
 * How old a queued item is, in days, or `undefined` when its timestamp is
 * unreadable. An unknown age must never drop an item — see
 * `shouldDropUnpublishItem`, whose age arm needs a real number to fire.
 */
export function queuedItemAgeDays(raw: unknown, now: DateTime): number | undefined {
  const iso = readQueuedAtIso(raw);
  return iso === undefined ? undefined : now.diff(DateTime.fromISO(iso)).as('days');
}

/** What a single drain attempt on one queued unpublish means for that item. */
export enum UnpublishReplayFate {
  /** the server removed the shared copy; the item has done its job */
  Done = 'done',
  /** the server was never reached, so nothing was learned — keep the item and do NOT burn an attempt */
  KeepWithoutCountingAttempt = 'keep-without-counting-attempt',
  /** the server was reached and did not remove it — keep the item, but count this against the cap */
  KeepAndCountAttempt = 'keep-and-count-attempt',
}

/**
 * Which of the three fates one drain attempt earned.
 *
 * `removed` is the server answering `{ ok: true }`. Everything else is a
 * failure, and the ONLY thing that decides how it is treated is whether the
 * server was reached at all:
 *
 * - **Not reached** (offline again, or the call's own retries ran out against a
 *   dead connection). We learned nothing about the food, so the item keeps its
 *   full allowance of attempts. This is the case the whole outbox exists for.
 * - **Reached, and it refused or failed.** The refusals `unpublishFood_impl`
 *   can give — the food is not in the shared database, it is not this user's,
 *   it is one of our official foods — are all deterministic, so retrying will
 *   get the same answer. They are not told apart here, deliberately: the server
 *   distinguishes them only in the wording of an error string, and
 *   `unpublish-outcome.ts` explains at length why matching those sentences is a
 *   trap. So the item retries a bounded number of times and is then dropped with
 *   a report that says what the server last said, rather than a client guessing
 *   from prose. What that costs is a food that was already gone sitting in the
 *   queue for a while; each attempt is one small call on a reconnect that was
 *   happening anyway.
 */
export function decideUnpublishReplayFate(
  input: { removed: boolean; serverWasReached: boolean },
): UnpublishReplayFate {
  if (input.removed) {
    return UnpublishReplayFate.Done;
  }
  return input.serverWasReached
    ? UnpublishReplayFate.KeepAndCountAttempt
    : UnpublishReplayFate.KeepWithoutCountingAttempt;
}
